import dynamic from 'next/dynamic'
import useTranslation from 'next-translate/useTranslation'
import { twMerge } from 'tailwind-merge'

import { getSimpleAddress } from '@unreserved-frontend-v2/geolocation/utils/utils'
import { MediaProps } from '@unreserved-frontend-v2/modules/listings/components/[tab]/media/utils/media-props'
import { ImageWithShimmerOptimized } from '@unreserved-frontend-v2/ui/image-with-shimmer/image-with-shimmer-optimized'
import { LightGalleryWrapperDesktopProps } from '@unreserved-frontend-v2/ui/light-gallery/light-gallery-wrapper-desktop'

import { useGalleryDesktop } from './utils/use-gallery-desktop'

const LightGalleryWrapperDesktop: React.ComponentType<LightGalleryWrapperDesktopProps> = dynamic(
  () =>
    import('@unreserved-frontend-v2/ui/light-gallery/light-gallery-wrapper-desktop').then(
      (mod) => mod.LightGalleryWrapperDesktop
    ),
  { ssr: false }
)

// ⚠️ Changing this distribution will also need an update in the grid.
const IMAGES_PER_ROW = 5
const IMAGE_PROPERTIES = {
  sm: {
    w: 264,
    h: 230,
  },
  lg: {
    w: 650,
    h: 575,
  },
}

export function MediaDesktop(mediaProps: MediaProps) {
  const { openGallery, onInit, lightBoxItems, onAfterAppendSubHtml, galleryHeader } = useGalleryDesktop(mediaProps)
  const { t } = useTranslation()

  const getThumbnail = (image: { url: string }, size: 'sm' | 'lg', index: number) => {
    if (!image) {
      return null
    }
    return (
      <div onClick={() => openGallery(index)}>
        <ImageWithShimmerOptimized
          className={`cursor-pointer overflow-hidden rounded-[5px] ${size === 'sm' ? 'h-[230px]' : 'h-[475px]'}`}
          src={image.url}
          width={IMAGE_PROPERTIES[size].w}
          height={IMAGE_PROPERTIES[size].h}
          isRestricted={undefined}
          alt={`${getSimpleAddress(mediaProps.address)} - ${t('ui:of', {
            current: index + 1,
            total: media?.length,
          })}`}
        />
      </div>
    )
  }

  const media = mediaProps.media

  const mediaCollection: {
    image: {
      url: string
      title?: string | null
      id: string
    }
    slideIndex: number
  }[][] = []
  media?.forEach((m, index) => {
    if (index % IMAGES_PER_ROW === 0) {
      mediaCollection.push([
        {
          image: {
            url: m.originUrl,
            title: m.title,
            id: m.id,
          },
          slideIndex: index,
        },
      ])
    } else {
      mediaCollection[mediaCollection.length - 1].push({
        image: {
          id: m.id,
          url: m.originUrl,
          title: m.title,
        },
        slideIndex: index,
      })
    }
  })

  return (
    <div className="space-y-3 pb-10">
      {mediaCollection.map((row, index) => (
        <div key={row[0]?.image.id} className="grid grid-cols-11 gap-3">
          <div className={twMerge('col-span-6', index % 2 === 0 ? 'order-1' : 'order-2')}>
            {getThumbnail(row[0]?.image, 'lg', row[0]?.slideIndex)}
          </div>
          <div className={twMerge('col-span-5 grid grid-cols-2 gap-3', index % 2 === 1 ? 'order-1' : 'order-2')}>
            {getThumbnail(row[1]?.image, 'sm', row[1]?.slideIndex)}
            {getThumbnail(row[2]?.image, 'sm', row[2]?.slideIndex)}
            {getThumbnail(row[3]?.image, 'sm', row[3]?.slideIndex)}
            {getThumbnail(row[4]?.image, 'sm', row[4]?.slideIndex)}
          </div>
        </div>
      ))}
      <LightGalleryWrapperDesktop
        dynamicEl={lightBoxItems}
        onInit={onInit}
        galleryHeader={galleryHeader}
        onAfterAppendSubHtml={onAfterAppendSubHtml}
      />
    </div>
  )
}

export default MediaDesktop
